import Splide from '@splidejs/splide';
import { getBreakpointOptions, setContentHeight } from '@utils/sliderLayout';
import VideoControls from '@utils/videoControls';

export default class MediaGallery {
  private context: Element;
  private splideEl: HTMLElement;
  private splide: Splide;
  private playbackMode: string;
  private mobileQuery: MediaQueryList;
  private arrowClicked = false;

  constructor(context: Element) {
    this.context = context;
    this.splideEl = context.querySelector('.splide');
    this.playbackMode = this.splideEl?.dataset.playbackMode || 'user_selected';
    this.mobileQuery = window.matchMedia('(max-width: 749px)');

    this.initSlider();
  }

  /**
   * Initializes the Splide slider and sets up video playback after mount.
   */
  private initSlider() {
    const sliderContainer = this.context.querySelector(
      '.js-media-gallery-slider'
    );
    if (!sliderContainer || !this.splideEl) return;

    const splideId = this.splideEl.id;
    this.splide = new Splide(`#${splideId}`, {
      type: 'slide',
      lazyLoad: false,
      perMove: 1,
      arrows: true,
      preloadPages: 2,
      role: 'group',
      mediaQuery: 'min',
      focus: 0,
      omitEnd: true,
      breakpoints: getBreakpointOptions(this.splideEl),
    });

    this.splide.on('mounted', () => {
      this.splideEl.classList.add('content--loaded');
      setContentHeight(this.splideEl);
      this.setupVideoPlayback();
      this.initVideoControls();
    });

    this.splide.mount();
  }

  /**
   * Wires up VideoControls for each card's custom play/pause button.
   */
  private initVideoControls() {
    this.context.querySelectorAll('.js--video-controls').forEach((controls) => {
      new VideoControls(controls as HTMLElement, controls.parentElement);

      controls
        .querySelector('.icon--video-play')
        ?.addEventListener('click', () => {
          this.pauseAllVideos();
        });
    });
  }

  /**
   * Returns the <video> element within a slide, or null if the slide has no video.
   */
  private getVideo(slide: Element): HTMLVideoElement | null {
    return slide.querySelector('video');
  }

  /**
   * Pauses all videos in the module.
   */
  private pauseAllVideos() {
    this.context
      .querySelectorAll<HTMLElement>('.splide__slide')
      .forEach((slide) => {
        this.getVideo(slide)?.pause();
        this.markControlsPaused(slide);
      });
  }

  /**
   * Plays the video in the given slide if one exists.
   */
  private playSlideVideo(slide: Element) {
    const video = this.getVideo(slide);
    if (video) {
      this.pauseAllVideos();
      this.markControlsPlay(slide);
      video.play();
    }
  }

  /**
   * Sets the visual state of a card's custom controls button to paused.
   */
  private markControlsPaused(slide: Element) {
    slide
      .querySelector('.js--video-controls')
      ?.classList.add('video-controls--paused');
  }

  /**
   * Sets the visual state of a card's custom controls button to playing.
   */
  private markControlsPlay(slide: Element) {
    slide
      .querySelector('.js--video-controls')
      ?.classList.remove('video-controls--paused');
  }

  /**
   * Plays the first visible slide that contains a video, pausing all others.
   */
  private playFirstVisibleVideo() {
    const visibleSlides = Array.from(
      this.splideEl.querySelectorAll<HTMLElement>('.splide__slide.is-visible')
    );
    const firstWithVideo = visibleSlides.find(
      (slide) => this.getVideo(slide) !== null
    );
    if (firstWithVideo) this.playSlideVideo(firstWithVideo);
  }
  /**
   * Orchestrates all video playback logic after Splide has mounted.
   */
  private setupVideoPlayback() {
    const slides = Array.from(
      this.context.querySelectorAll<HTMLElement>('.splide__slide')
    );
    if (!slides.length) return;

    if (this.playbackMode === 'user_selected') {
      // Mark all slides after the first as paused so controls show the correct state
      slides.slice(1).forEach((slide) => this.markControlsPaused(slide));

      slides.forEach((slide) => {
        const video = this.getVideo(slide);
        video?.addEventListener('ended', () => this.markControlsPaused(slide));
      });
    }

    if (this.playbackMode === 'autoplay_sequence') {
      this.setupAutoplaySequence(slides);
    }

    // Defer initial autoplay until the module scrolls into view
    const observer = new IntersectionObserver(
      (entries, obs) => {
        if (entries[0].isIntersecting) {
          this.playSlideVideo(slides[0]);
          obs.disconnect();
        }
      },
      { threshold: 0.25 }
    );
    observer.observe(this.context);

    this.splideEl.querySelectorAll('.splide__arrow').forEach((arrow) => {
      arrow.addEventListener('click', () => {
        this.arrowClicked = true;
      });
    });

    // On mobile: play the newly active slide's video after transition.
    // On desktop in autoplay_sequence: play the first visible video when an arrow was clicked.
    this.splide.on('moved', (newIndex: number) => {
      if (this.mobileQuery.matches) {
        this.playSlideVideo(slides[newIndex]);
      } else if (
        this.playbackMode === 'autoplay_sequence' &&
        this.arrowClicked
      ) {
        this.arrowClicked = false;
        this.playFirstVisibleVideo();
      }
    });
  }

  /**
   * Attaches `ended` listeners to each video so the slider advances automatically
   * when a video finishes. On mobile, Splide moves first and the `moved` handler
   * plays the next video. On desktop, `go()` triggers `moved` which plays it.
   */
  private setupAutoplaySequence(slides: HTMLElement[]) {
    slides.forEach((slide, index) => {
      const video = this.getVideo(slide);
      if (!video) return;

      video.addEventListener('ended', () => {
        const nextVideoIndex = slides.findIndex(
          (slide, i) => i > index && this.getVideo(slide) !== null
        );
        if (nextVideoIndex === -1) {
          this.markControlsPaused(slide);
          return;
        }

        this.splide.go(nextVideoIndex);

        if (!this.mobileQuery.matches) {
          // On desktop, play directly — `moved` may not fire if all slides
          // are already in view and Splide cannot actually scroll.
          this.playSlideVideo(slides[nextVideoIndex]);
        }
        // On mobile, the `moved` handler takes over after the transition.
      });
    });
  }
}
